<script setup lang="ts">
import {useI18n} from 'vue-i18n';
import {
  type PropType,
  ref,
  watch,
  computed
} from 'vue';
import {type Svg} from '@/types/assets';
import {type DataSourceRankedMetric} from '@/types/webContracts';
import SvgComponent from '@/components/Svg.vue';
import PiqGraph from 'src/pages/DashboardMyStats/components/PiqGraph.vue';
import {comparison} from '@/assets';
import {useSessionStore} from '@/store/sessionStore';

const {t} = useI18n();
const sessionStore = useSessionStore();

const visible = defineModel('visible')
const graphComponent = ref();

const props = defineProps({
  stat: String,
  title: String,
  sourceType: String,
  deviceId: { type: Number, required: false },
  measurementType: { type: String, required: false },
  measurementUnit: String,
  headingIcon: {
    type: Object as PropType<Svg | string | undefined>,
    default: undefined
  },
  headingTotal: { type: String, default: '--' },
  headingDate: String,
  piq: String,
  lastMonth: { type: String, default: '--' },
  lastWeek: { type: String, default: '--' },
  avgPerDay: { type: String, default: '--' },
  avgPerDayTrending: { type: String, default: '--' },
  fiveDayAvg: { type: String, default: '--' },
  fiveDayAvgTrending: { type: String, default: '--' },
  readySignal: Number,
  rankedComparison: { type: Object as PropType<DataSourceRankedMetric | null | undefined> }
});

watch(() => props.readySignal, (nV) => {
  if (nV > 1) {
    graphComponent.value.reset()
  }
})

const isSurgicalProcedure = computed(() => sessionStore.selectedProcedure?.isSurgicalProcedure);

const preSurgeryLabel = computed(() => {
  return isSurgicalProcedure.value ? 'education_pre_surgery' : 'pre_treatment';
});

const postSurgeryLabel = computed(() => {
  return isSurgicalProcedure.value ? 'education_post_surgery' : 'during_treatment';
});

function toggleDetails() {
  visible.value = !visible.value
  graphComponent.value.initialize()
}
</script>

<template>
  <div :class="['the-expandable-my-stat', stat]">
    
    <!-- Title -->
    <div class="row the-expandable-my-stat__title mb-8">
      <div class="col">
        {{ t(title) }}<template v-if="measurementUnit"> ({{ t(measurementUnit) }})</template>
      </div>
    </div>

    <q-card flat bordered class="the-expandable-my-stat__data">

      <!-- Heading -->
      <q-card-section class="row px-none items-center the-expandable-my-stat__title">
        
        <!-- Icon -->
        <div class="col-auto px-16 data__heading-icon">
          <template v-if="props.headingIcon">
            <q-icon v-if="typeof props.headingIcon === 'string'"
                    :name="props.headingIcon" />
            <SvgComponent v-else-if="typeof props.headingIcon === 'object'"
                          :vector-paths="props.headingIcon?.vectorPaths"
                          :view-box="props.headingIcon?.viewBox" />
          </template>
        </div>
        
        <!-- Total -->
        <div class="col-auto pr-16 data__heading-total">{{ headingTotal }}</div>
        
        <!-- Date -->
        <div class="col-auto pr-16 data__heading-date">{{ piq ? headingDate : t(headingDate) }}</div>
      </q-card-section>

      <!-- Averages -->
      <q-card-section class="row data__averages px-none items-center">
        
        <!-- Left -->
        <div class="col pa-16">
          
          <!-- Type -->
          <p v-if="piq">{{ t('stats_last_month_piq') }}</p>
          <p v-else>{{ t(preSurgeryLabel) }}</p>

          <!-- Value -->
          <p class="data__averages-value">
            {{ piq ? lastMonth : avgPerDay
            }}<q-icon v-if="!piq && avgPerDayTrending"
                    :color="avgPerDayTrending === 'up' ? 'positive' : avgPerDayTrending === 'down' ? 'negative' : undefined"
                    :name="`fa-regular ${avgPerDayTrending === 'up' ? 'fa-arrow-up-long' : avgPerDayTrending === 'down' ? 'fa-arrow-down-long' : '' }`"
                    right
                    size="30px" />
          </p>

          <!-- Unit -->
          <p v-if="piq">{{ t('stats_average_piq') }}</p>
          <p v-else>{{ t('stats_steps_day_avg') }}</p>
        </div>
        
        <!-- Right -->
        <div class="col pr-16">
          
          <!-- Type -->
          <p v-if="piq">{{ t('stats_last_week_piq') }}</p>
          <p v-else>{{ t(postSurgeryLabel) }}</p>

          <!-- Value -->
          <p class="data__averages-value">
            {{ piq ? lastWeek : fiveDayAvg
            }}<q-icon v-if="!piq && fiveDayAvgTrending"
                    :color="fiveDayAvgTrending === 'up' ? 'positive' : fiveDayAvgTrending === 'down' ? 'negative' : undefined"
                    :name="`fa-regular ${fiveDayAvgTrending === 'up' ? 'fa-arrow-up-long' : fiveDayAvgTrending === 'down' ? 'fa-arrow-down-long' : '' }`"
                    right
                    size="30px" />
          </p>

          <!-- Unit -->
          <p v-if="piq">{{ t('stats_average_piq') }}</p>
          <p v-else>{{ t('stats_steps_5day_avg') }}</p>
        </div>
      </q-card-section>
      
      <!-- Show/Hide Details -->
      <q-card-section class="row px-none py-30 justify-center" v-show="visible">
        <div class="col">
          <PiqGraph class="col" :measurementType="measurementType" :source="sourceType" :device="deviceId"
            ref="graphComponent" />
        </div>
      </q-card-section>
      <q-card-section v-if="rankedComparison"
                      class="row justify-center px-none py-12 the-expandable-my-stat__comparison">
        <div class="col-auto">
          <zbm-btn class="text-weight-regular font-size-16"
                   :outline="false"
                   :rounded="false"
                   text-color="accent"
                   :to="{ name: 'Dashboard.MyStats.MyProgress', params: { deviceId: props.deviceId, metric: rankedComparison.comparisonDataType } }">
            <SvgComponent class="mr-8"
                          style="width: 20px; height: auto"
                          :vector-paths="comparison.vectorPaths"
                          :view-box="comparison.viewBox" />
            {{ t('stats_view_comparisons') }}
          </zbm-btn>
        </div>
      </q-card-section>
      <q-card-section class="row justify-center px-none py-12 the-expandable-my-stat__view-details">
        <div class="col-auto">
          <zbm-btn @click="toggleDetails"
                   class="text-weight-regular font-size-16"
                   icon="fa-solid fa-chart-bar"
                   :label="t('stats_view_activity')"
                   :outline="false"
                   :rounded="false"
                   text-color="accent" />
        </div>
      </q-card-section>
    </q-card>
  </div>
</template>